// V2 Adoption dashboard — platform-admin internal surface
// (CHG-1307 / ITER-3434).
//
// Mounts at /platform/v2-adoption. ProtectedRoute (CHG-1289) wraps the
// route entry in App.tsx with the platform-admin permission code so
// non-admins land on AccessDeniedPage; this page assumes it's already
// past that gate and focuses on data orchestration.
//
// Thin consumer of the MetricsDashboard composition primitive — the
// page owns the react-query fetch + retry state; the primitive owns
// layout.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MetricsDashboard } from "../../components/composition/MetricsDashboard";
import { v2AdoptionApi } from "../../api/v2-adoption";

export function V2AdoptionDashboard() {
  const [orgFilter, setOrgFilter] = useState<string>("");
  const trimmedOrg = orgFilter.trim() || null;

  const query = useQuery({
    queryKey: ["platform", "v2-adoption", trimmedOrg],
    queryFn: () => v2AdoptionApi.fetch(trimmedOrg),
  });

  const data = query.data;

  return (
    <MetricsDashboard
      title="v2 Adoption"
      subtitle="Platform-admin internal dashboard for measuring v1 ↔ v2 dashboard adoption (CHG-1287 telemetry)."
      filterCaption={
        <FilterRow
          orgFilter={orgFilter}
          onOrgFilterChange={setOrgFilter}
          generatedAt={data?.generated_at}
        />
      }
      statStrip={
        data?.stat_strip ?? {
          window_days: 7,
          v1_total: 0,
          v2_total: 0,
          by_brand: [],
        }
      }
      timeSeries={
        data?.time_series ?? {
          window_days: 30,
          daily: [],
        }
      }
      funnel={
        data?.funnel ?? {
          window_days: 30,
          steps: [],
        }
      }
      loading={query.isLoading}
      error={query.error as Error | null}
      onRetry={() => query.refetch()}
    />
  );
}

function FilterRow({
  orgFilter,
  onOrgFilterChange,
  generatedAt,
}: {
  orgFilter: string;
  onOrgFilterChange: (v: string) => void;
  generatedAt?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <label
        style={{ fontSize: 12, color: "var(--color-text-muted)" }}
      >
        org_id filter:{" "}
        <input
          type="text"
          name="org_id"
          aria-label="Filter by organization ID"
          value={orgFilter}
          onChange={(e) => onOrgFilterChange(e.target.value)}
          placeholder="optional uuid"
          style={{
            marginLeft: 6,
            padding: "4px 8px",
            fontSize: 12,
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-sm)",
            width: 280,
          }}
        />
      </label>
      {generatedAt ? (
        <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
          generated {new Date(generatedAt).toISOString()}
        </span>
      ) : null}
    </div>
  );
}
